/**
 * FileDrawer — slide-in preview for a single attachment.
 * Photos and PDFs render inline (via an authenticated blob: URL); other kinds
 * show a friendly hero with download / open-in-new-tab actions.
 */

import React, { useEffect, useState } from 'react';
import { XMarkIcon, ArrowDownTrayIcon, ArrowTopRightOnSquareIcon, PhotoIcon } from '@heroicons/react/24/outline';
import type { BoardAttachment } from './types';
import { ATTACH_META, fmtBytes } from './boardHelpers';
import { attachmentBlobUrl, attachmentDownloadUrl } from './api';

const SUBTITLE: Record<string, string> = {
  pdf: 'Document PDF',
  email: 'Message e-mail',
  photo: 'Photographie',
  word: 'Document Word',
  excel: 'Classeur Excel',
  file: 'Fichier',
};

export const FileDrawer: React.FC<{
  boardId: string;
  file: BoardAttachment;
  siblings: BoardAttachment[];
  onClose: () => void;
  onSelect: (a: BoardAttachment) => void;
}> = ({ boardId, file, siblings, onClose, onSelect }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  const meta = ATTACH_META[file.kind] || ATTACH_META.file;
  const Icon = meta.Icon;
  const previewable = file.kind === 'photo' || file.kind === 'pdf';
  const photos = siblings.filter(s => s.kind === 'photo');

  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, [onClose]);

  useEffect(() => {
    let live = true;
    setUrl(null); setErr(false);
    if (previewable) {
      attachmentBlobUrl(boardId, file.id)
        .then(u => { if (live) setUrl(u); })
        .catch(() => { if (live) setErr(true); });
    }
    return () => { live = false; };
  }, [boardId, file.id, previewable]);

  const sub = `${SUBTITLE[file.kind] || 'Fichier'}${file.size ? ` · ${fmtBytes(file.size)}` : ''}`;

  return (
    <>
      <div className="bd-scrim" onClick={onClose} />
      <div className="bd-drawer" role="dialog" aria-label={file.name}>
        <div className="bd-dr-hd">
          <span className={`bd-dr-ic ${meta.cls}`}><Icon className="w-5 h-5" /></span>
          <div className="bd-dr-meta">
            <div className="bd-dr-name">{file.name}</div>
            <div className="bd-dr-sub">{sub}</div>
          </div>
          <button className="bd-dr-x" onClick={onClose} aria-label="Fermer"><XMarkIcon className="w-5 h-5" /></button>
        </div>

        <div className="bd-dr-body">
          {file.kind === 'photo' && (
            <>
              <div className="bd-photo-big">
                {url ? <img src={url} alt={file.name} /> : <div className="bd-ph-load">{err ? 'Aperçu indisponible' : 'Chargement…'}</div>}
              </div>
              {photos.length > 1 && (
                <div className="bd-photo-grid">
                  {photos.map(p => (
                    <button key={p.id} className={`bd-ph-cell ${p.id === file.id ? 'bd-ph-cell--active' : ''}`} onClick={() => onSelect(p)}>
                      <ThumbImg boardId={boardId} att={p} />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {file.kind === 'pdf' && (
            url
              ? <iframe className="bd-pdf-frame" src={url} title={file.name} />
              : <div className="bd-ph-load">{err ? 'Aperçu indisponible' : 'Chargement du document…'}</div>
          )}

          {!previewable && (
            <div className="bd-doc-hero">
              <div className={`bd-doc-bigic ${meta.cls}`}><Icon className="w-8 h-8" /></div>
              <h3>{file.name}</h3>
              <p>{SUBTITLE[file.kind] || 'Fichier'}{file.size ? ` · ${fmtBytes(file.size)}` : ''}</p>
            </div>
          )}
        </div>

        <div className="bd-dr-foot">
          <a className="bd-btn bd-btn-primary" href={attachmentDownloadUrl(boardId, file.id)} target="_blank" rel="noreferrer">
            <ArrowDownTrayIcon className="w-4 h-4" /> Télécharger
          </a>
          {url && (
            <a className="bd-btn" href={url} target="_blank" rel="noreferrer">
              <ArrowTopRightOnSquareIcon className="w-4 h-4" /> Ouvrir
            </a>
          )}
        </div>
      </div>
    </>
  );
};

/** Small thumbnail that lazily resolves its own blob URL. */
export const ThumbImg: React.FC<{ boardId: string; att: BoardAttachment }> = ({ boardId, att }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    setUrl(null); setErr(false);
    attachmentBlobUrl(boardId, att.id)
      .then(u => { if (live) setUrl(u); })
      .catch(() => { if (live) setErr(true); });
    return () => { live = false; };
  }, [boardId, att.id]);
  if (err) return <span className="bd-thumb-ph bd-thumb-ph--err" title="Aperçu indisponible"><PhotoIcon className="w-4 h-4" /></span>;
  if (!url) return <span className="bd-thumb-ph" />;
  return <img src={url} alt={att.name} onError={() => setErr(true)} />;
};
